import React, { Fragment } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  FormDataConsumer,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  TextInput
} from "react-admin";
import CustomDateRangeInput from "../../../components/CustomDateRangeInput";
import {
  includeParentOptionList,
  ScheduleTypeEnum,
  scheduleTypeList,
} from "../../students/const";
import Alert from "@material-ui/lab/Alert";
import {
  IncludeStudentOptionEnum,
  includeStudentOptionList,
  isTaggedCreditNoteList,
} from "../conts";
import CustomAcademicYearInput from "../../../components/CustomAcademicYearInput";
import CustomCcaAcademicYearInput from "../../../components/CustomCcaAcademicYearInput";
import { makeStyles, Typography } from "@material-ui/core";
import { useField } from "react-final-form";
import moment from "moment/moment";
import InfoIcon from "@material-ui/icons/Info";
import CustomTooltip from "../../../components/CustomTooltip";

interface IExportStudentInput {
  documentType: "Invoice" | "CreditNote";
  isDisabled?: boolean;
}

const useStyles = makeStyles({
  exportToggleInput: {
    marginTop: "10px",
    marginBottom: "-25px"
  },
  alertToggle: {
    margin: "10px 0px"
  },
  captionTooltip: {
    marginTop: "-5px",
    marginBottom: "5px",
  },
  captionText: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "5px",
  },
  infoIcon: {
    fontSize: "1.15rem",
  },
});

const IsFilterBySchoolMbcCodeInput = ({ isDisabled }: any) => {
  const classes = useStyles();

  const includeStudentOptionField = useField("includeStudentOption");
  const academicYearIdField = useField("academicYearId");
  const ccaAcademicYearIdField = useField("ccaAcademicYearId");

  return (
    <Fragment>
      <BooleanInput
        className={classes.exportToggleInput}
        source="isFilterBySchool"
        label="Filter by School MBC Code"
        defaultValue={false}
        onChange={(e: any) => {
          includeStudentOptionField && includeStudentOptionField.input.onChange("");
        }}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterBySchool && (
            <ReferenceInput
              source="schoolId"
              label="School Code"
              reference="schools"
              filter={{ MbcRegisteredOnly: true }}
              disabled={isDisabled}
              onChange={({ target }) => {
                academicYearIdField && academicYearIdField.input.onChange("");
                ccaAcademicYearIdField && ccaAcademicYearIdField.input.onChange("");
              }}
            >
              <AutocompleteInput
                optionText={(choice: any) =>
                  `${choice.mbcCode} - ${choice.name}`
                }
              />
            </ReferenceInput>
          )
        }
      </FormDataConsumer>
    </Fragment>
  )
}

const ExportCommercialDocumentInput = ({ documentType, isDisabled }: IExportStudentInput) => {
  const classes = useStyles();

  return (
    <Fragment>
      <Typography variant="subtitle1" className={classes.captionText}>
        Issued Date Range
        <CustomTooltip
          title={
            <Typography variant="caption">
              The issued date range cannot be longer than 3 months
            </Typography>
          }
          classes={{
            tooltip: classes.captionTooltip,
          }}
          placement="bottom-start"
        >
          <InfoIcon color="secondary" className={classes.infoIcon} />
        </CustomTooltip>
      </Typography>
      <FormDataConsumer>
        {({ formData }) => (
          <CustomDateRangeInput
            fromSource="startDate"
            toSource="endDate"
            fromLabel="Start Date"
            toLabel="End Date"
            toMaxDate={moment
              .utc(formData.startDate)
              .add(3, "months")
              .format("yyyy-MM-DD")}
            isDisabled={isDisabled}
            gridItemWidth={6}
          />
        )}
      </FormDataConsumer>

      <BooleanInput
        className={classes.exportToggleInput}
        source="isFilterByStatus"
        label="Filter by Status"
        defaultValue={false}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterByStatus && (
            <>
              <SelectArrayInput
                source="status"
                choices={[
                  { id: "Draft", name: "Draft" },
                  { id: "Unposted", name: "Unposted" },
                  { id: "Posted", name: "Posted" },
                ]}
                disabled={isDisabled}
              />

              {formData.status?.includes("Posted") &&
                documentType === "CreditNote" && (
                  <SelectInput
                    label="Is Tagged Credit Note"
                    source="isTaggedCreditNote"
                    choices={isTaggedCreditNoteList}
                    allowEmpty
                  />
              )}
            </>
          )
        }
      </FormDataConsumer>

      <IsFilterBySchoolMbcCodeInput isDisabled={isDisabled} />

      <BooleanInput
        className={classes.exportToggleInput}
        source="isFilterByDescription"
        label="Filter by Description"
        defaultValue={false}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterByDescription && (
            <TextInput source="description" />
          )
        }
      </FormDataConsumer>

      <BooleanInput
        className={classes.exportToggleInput}
        source="isIncludeParent"
        label="Include Parent"
        defaultValue={false}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeParent && (
            <SelectInput
              source="includeParentOption"
              label="Include Parent Options"
              choices={includeParentOptionList}
              disabled={isDisabled}
            />
          )
        }
      </FormDataConsumer>

      <BooleanInput
        className={classes.exportToggleInput}
        source="isIncludeStudent"
        label="Include Student"
        defaultValue={false}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeStudent &&
          (!formData.isFilterBySchool || !formData.schoolId) && (
            <Alert
              severity="info"
              className={classes.alertToggle}
            >
              You need to enable <strong>Filter By School MBC Code</strong>,{" "}
              if you want to include <strong>Student With Schedule Period</strong>
            </Alert>
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) => {
          if (!formData.isIncludeStudent) {
            return <></>
          }

          const optionChoices = includeStudentOptionList.map(choice => {
            if (choice.id === IncludeStudentOptionEnum.StudentWithSchedulePeriod) {
              return {
                ...choice,
                disabled: !formData.isFilterBySchool || !formData.schoolId, // disable if school filter is not checked
              };
            }
            return choice;
          });

          return (
            <SelectInput
              source="includeStudentOption"
              label="Include Student Options"
              choices={optionChoices}
              disabled={isDisabled}
            />
          )
        }}
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod && (
            <SelectInput
              source="scheduleType"
              label="Schedule Type"
              choices={scheduleTypeList}
              disabled={isDisabled}
            />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod &&
          (formData.scheduleType === ScheduleTypeEnum.Regular ||
            formData.scheduleType === ScheduleTypeEnum.Both) && (
            <CustomAcademicYearInput
              schoolId={formData.schoolId}
              isDisabled={isDisabled}
            />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.includeStudentOption === IncludeStudentOptionEnum.StudentWithSchedulePeriod &&
          (formData.scheduleType === ScheduleTypeEnum.Cca ||
            formData.scheduleType === ScheduleTypeEnum.Both) && (
            <CustomCcaAcademicYearInput
              schoolId={formData.schoolId}
              isDisabled={isDisabled}
            />
          )
        }
      </FormDataConsumer>
    </Fragment>
  )
}

export default ExportCommercialDocumentInput;